import React, { useMemo, useState } from 'react';
import {
  FiActivity,
  FiAlertCircle,
  FiArrowRight,
  FiCheckCircle,
  FiDatabase,
  FiEye,
  FiEyeOff,
  FiLock,
  FiMail,
  FiServer,
  FiShield
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { gatewayApi, storeSession } from '../services/gateway';

const statusTone = (ready) => ready
  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
  : 'bg-amber-50 text-amber-700 border-amber-200';

const AdminLogin = ({ backendStatus, onAuthenticated }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const gatewayReady = backendStatus?.gateway === 'operational';
  const pocketBaseReady = backendStatus?.pocketbase === 'connected';

  const statusText = useMemo(() => ({
    gateway: gatewayReady
      ? 'Email runtime ready'
      : backendStatus?.gateway === 'unreachable'
        ? 'Mail hook unavailable'
        : 'Checking mail gateway',
    pocketbase: pocketBaseReady
      ? 'PocketBase API connected'
      : backendStatus?.pocketbase === 'unreachable'
        ? 'PocketBase API unavailable'
        : 'Checking PocketBase API'
  }), [backendStatus, gatewayReady, pocketBaseReady]);

  const submit = async (event) => {
    event.preventDefault();
    if (!email.trim() || !password) return;

    setIsSubmitting(true);
    setError('');

    let pocketBaseAuthenticated = false;
    try {
      const response = await gatewayApi.login(email.trim(), password);
      pocketBaseAuthenticated = true;
      storeSession(response.data.token);
      // PocketBase password authentication and protected Mail Hub hook
      // availability are separate checks. A broken hook must NEVER be
      // described as a wrong password.
      const verified = await gatewayApi.checkAdmin();
      onAuthenticated({...response.data.admin,...verified.data});
    } catch (err) {
      if (pocketBaseAuthenticated) {
        if (err.status === 403) {
          storeSession('');
          setError('PocketBase accepted your password, but this account is not authorised to administer the Email Gateway. Check the account verification and administrator allowlist.');
        } else {
          // Keep the valid session token during a transient backend outage.
          // The app only grants access after checkAdmin succeeds.
          setError('PocketBase accepted your password, but the Email Gateway server is not responding correctly. Restore or check pb_hooks/mail_service.js on PocketHost. Your password does not need resetting.');
        }
      } else {
        storeSession('');
        if (err.status === 400 || err.status === 401) {
          setError('PocketBase did not accept the username and password. Check the selected account, authentication collection and account password.');
        } else {
          setError(err.message || 'Could not reach PocketBase to authenticate.');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-slate-900">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 -top-32 h-[34rem] w-[34rem] rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute -bottom-40 right-0 h-[30rem] w-[30rem] rounded-full bg-violet-600/20 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.08),transparent_32%)]" />
      </div>

      <div className="relative mx-auto flex min-h-screen w-full max-w-[1440px] items-center px-5 py-8 sm:px-8 lg:px-12">
        <div className="grid w-full overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.04] shadow-2xl shadow-black/40 backdrop-blur-xl lg:grid-cols-[1.05fr_0.95fr]">
          <section className="relative hidden min-h-[720px] overflow-hidden border-r border-white/10 p-12 lg:flex lg:flex-col lg:justify-between">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-violet-600/10" />

            <div className="relative">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 text-xl font-black text-white shadow-lg shadow-blue-950/30">
                  B
                </div>
                <div>
                  <p className="text-sm font-black tracking-[0.18em] text-white">BPA SOLUTIONS</p>
                  <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                    Infrastructure Console
                  </p>
                </div>
              </div>

              <div className="mt-24 max-w-xl">
                <span className="inline-flex items-center gap-2 rounded-full border border-blue-400/20 bg-blue-400/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-300">
                  <SafeIcon icon={FiShield} />
                  Secure administration
                </span>

                <h1 className="mt-6 text-5xl font-black leading-[1.05] tracking-tight text-white">
                  Control the email gateway from one secure console.
                </h1>

                <p className="mt-6 max-w-lg text-base leading-7 text-slate-400">
                  Manage clients, application origins, sender identities, BPA API keys, quotas and delivery activity without exposing provider credentials to the browser.
                </p>
              </div>
            </div>

            <div className="relative grid grid-cols-3 gap-3">
              <Feature icon={FiDatabase} title="PocketBase" subtitle="System of record" />
              <Feature icon={FiShield} title="Protected" subtitle="PocketBase mail gateway" />
              <Feature icon={FiActivity} title="Auditable" subtitle="Live operational logs" />
            </div>
          </section>

          <section className="flex min-h-[680px] items-center justify-center bg-white px-5 py-10 sm:px-10 lg:min-h-[720px] lg:px-14">
            <div className="w-full max-w-md">
              <div className="lg:hidden">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 text-lg font-black text-white shadow-lg">
                    B
                  </div>
                  <div>
                    <p className="text-sm font-black tracking-[0.12em] text-slate-900">BPA SOLUTIONS</p>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Email Gateway</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 lg:mt-0">
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-blue-600">Administrator access</p>
                <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950">Welcome back</h2>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  Sign in with your PocketBase user. Administrator access requires the BPA Mail Hub server hooks to be installed on the existing PocketHost instance.
                </p>
              </div>

              <div className="mt-7 grid grid-cols-2 gap-3">
                <StatusCard
                  icon={FiServer}
                  label="Mail Hub API"
                  text={statusText.gateway}
                  ready={gatewayReady}
                />
                <StatusCard
                  icon={FiDatabase}
                  label="PocketBase API"
                  text={statusText.pocketbase}
                  ready={pocketBaseReady}
                />
              </div>

              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">
                    Email address
                  </label>
                  <div className="relative mt-2">
                    <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      autoComplete="email"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3.5 pl-11 pr-4 text-sm font-medium text-slate-900 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-50"
                      placeholder="you@company.com"
                      autoFocus
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between gap-4">
                    <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">
                      Password
                    </label>
                    <span className="text-[10px] font-semibold text-slate-400">PocketBase credential</span>
                  </div>

                  <div className="relative mt-2">
                    <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      autoComplete="current-password"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3.5 pl-11 pr-12 text-sm font-medium text-slate-900 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-50"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(value => !value)}
                      className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="flex gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-100">
                      <SafeIcon icon={FiAlertCircle} />
                    </div>
                    <div>
                      <p className="text-xs font-black">Sign-in failed</p>
                      <p className="mt-1 text-xs font-medium leading-5">{error}</p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || !email.trim() || !password}
                  className="group flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-violet-600 px-5 py-3.5 text-sm font-black text-white shadow-lg shadow-blue-200 transition hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  <SafeIcon icon={isSubmitting ? FiServer : FiShield} className={isSubmitting ? 'animate-pulse' : ''} />
                  {isSubmitting ? 'Verifying PocketBase account...' : 'Sign in securely'}
                  {!isSubmitting && <SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-0.5" />}
                </button>
              </form>

              <div className="mt-7 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-600 shadow-sm ring-1 ring-slate-200">
                    <SafeIcon icon={FiCheckCircle} />
                  </div>
                  <div>
                    <p className="text-xs font-black text-slate-800">Credential handling</p>
                    <p className="mt-1 text-[11px] leading-5 text-slate-500">
                      Your password is sent directly over HTTPS to PocketBase for authentication. Only the PocketBase session token is kept for this browser session. Your password is never stored by this app.
                    </p>
                  </div>
                </div>
              </div>

              <p className="mt-6 text-center text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                BPA Solutions internal infrastructure
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

const Feature = ({ icon, title, subtitle }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-4 backdrop-blur-sm">
    <SafeIcon icon={icon} className="text-blue-300" />
    <p className="mt-3 text-xs font-black text-white">{title}</p>
    <p className="mt-1 text-[10px] text-slate-500">{subtitle}</p>
  </div>
);

const StatusCard = ({ icon, label, text, ready }) => (
  <div className={`rounded-2xl border p-3.5 ${statusTone(ready)}`}>
    <div className="flex items-center gap-2">
      <SafeIcon icon={icon} />
      <span className="text-[10px] font-black uppercase tracking-[0.12em]">{label}</span>
    </div>
    <p className="mt-2 truncate text-[11px] font-bold">{text}</p>
  </div>
);

export default AdminLogin;